import { LiveProvenanceBadge } from "@/components/provenance/LiveProvenanceBadge";
import { useKeyboardActivation } from "@/hooks/useKeyboardActivation";
import { sellerCartItemCount } from "@/utils/cart";
import { formatPrice } from "@/utils/money";
import { getSelectedShippingPickerId } from "@/utils/shippingOption";
import { pluralCount } from "@/utils/text";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { TruncateTooltip } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { ChevronDown, ChevronRight } from "lucide-react";
import { memo } from "react";
import { CartFreeShippingMeter } from "../CartFreeShippingMeter";
import { CartLineItem } from "../CartLineItem";

export interface SellerCartAccordionProps {
  /**
   * The seller cart data
   */
  sellerCart: SalonStockerHttpTypes.StoreSellerCart;
  /**
   * Whether this accordion is expanded
   */
  isExpanded: boolean;
  /**
   * Callback when expand/collapse is toggled
   */
  onToggle: () => void;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Expandable accordion section for a single seller's cart.
 * Shows seller name, item count, total when collapsed.
 * Expands to show line items and shipping info.
 */
export const SellerCartAccordion = memo(function SellerCartAccordion({
  sellerCart,
  isExpanded,
  onToggle,
  className
}: SellerCartAccordionProps) {
  const itemCount = sellerCartItemCount(sellerCart);
  const contentId = `seller-cart-accordion-content-${sellerCart.cart_id}`;

  // The header can't literally be a <button> - the provenance badge below is
  // a Tooltip trigger, a focusable descendant, and HTML5 forbids any
  // descendant with a tabindex inside a <button>. Same fix AccordionHeader
  // uses for its own nested Checkout button.
  const handleHeaderKeyDown = useKeyboardActivation(onToggle);

  return (
    <div className={cn("border-b border-gray-100 last:border-b-0", className)}>
      {/* Header - always visible */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={handleHeaderKeyDown}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        aria-label={
          isExpanded
            ? `Collapse ${sellerCart.seller.name} cart`
            : `Expand ${sellerCart.seller.name} cart`
        }
        className="flex w-full cursor-pointer items-center justify-between py-2 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-500"
      >
        <div className="flex min-w-0 items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
          )}
          <div className="min-w-0">
            <TruncateTooltip
              as="div"
              className="text-sm font-medium text-gray-900"
            >
              {sellerCart.seller.name}
            </TruncateTooltip>
            <div className="text-xs text-gray-500">
              {pluralCount(itemCount, "item")}
            </div>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1.5">
          <span className="text-sm font-semibold text-gray-900">
            {formatPrice(sellerCart.totals.total)}
          </span>
          <LiveProvenanceBadge
            provenance={sellerCart.totals.provenance}
            subject="total"
            source={sellerCart.seller.name}
          />
        </div>
      </div>

      {/* Free-freight meter - visible while building the basket, even collapsed.
          Renders nothing when there is no threshold or it is already met. */}
      <CartFreeShippingMeter
        cartId={sellerCart.cart_id}
        itemSubtotal={sellerCart.totals.item_subtotal}
        // Composite `base::handle` id for a live rate, so the meter scopes to
        // the selected service instead of falling back to the full list
        // (KAN-108).
        selectedShippingOptionId={getSelectedShippingPickerId(
          sellerCart.shipping_methods?.[0]
        )}
        className="mb-2"
      />

      {/* Expanded content */}
      {isExpanded && (
        <div id={contentId} className="pb-3 pl-6">
          {/* Line items */}
          <div className="divide-y divide-gray-50">
            {sellerCart.items.map((item) => (
              <CartLineItem
                key={item.id}
                item={item}
                cartId={sellerCart.cart_id}
                size="sm"
                showImage={true}
              />
            ))}
          </div>

          {/* Shipping info */}
          {sellerCart.shipping_methods.length > 0 && (
            <div className="mt-2 border-t border-gray-100 pt-2">
              <div className="text-xs text-gray-500">
                Shipping: {sellerCart.shipping_methods[0].name}
                {sellerCart.totals.shipping_total > 0 && (
                  <span className="ml-1">
                    ({formatPrice(sellerCart.totals.shipping_total)})
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
